import { X } from 'lucide-react-native';
import { useCallback, useState } from 'react';
import { Platform, Pressable, StyleSheet } from 'react-native';
import { Box, FormInput, HStack, Text, VStack } from '../../../components/primitives';
import { DatePickerInput } from '../../../components/primitives/DatePickerInput';
import { Select } from '../../../components/primitives/Select';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';

interface LeadFiltersProps {
  sourceFilter: string;
  onSourceChange: (value: string) => void;
  stageFilter: string;
  onStageChange: (value: string) => void;
  stageOptions: Array<{ value: string; label: string; color?: string }>;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  onSortChange: (sortBy: string, sortOrder: 'asc' | 'desc') => void;
  startDate: string;
  onStartDateChange: (value: string) => void;
  endDate: string;
  onEndDateChange: (value: string) => void;
  jobTitleFilter: string;
  onJobTitleChange: (value: string) => void;
  industryFilter: string;
  onIndustryChange: (value: string) => void;
  economicCapacityFilter: string;
  onEconomicCapacityChange: (value: string) => void;
  locationFilter: string;
  onLocationChange: (value: string) => void;
  tagFilter: string[];
  onTagChange: (value: string[]) => void;
  tagOptions: Array<{ value: string; label: string }>;
  onClearFilters: () => void;
  hasActiveFilters: boolean;
}

const SORT_OPTIONS = [
  { value: 'createdAt:desc', labelKey: 'leads.list.sortNewest' },
  { value: 'createdAt:asc', labelKey: 'leads.list.sortOldest' },
  { value: 'fullName:asc', labelKey: 'leads.list.sortName' },
  { value: 'company:asc', labelKey: 'leads.list.sortCompany' },
  { value: 'updatedAt:desc', labelKey: 'leads.list.sortUpdated' },
];

function useSourceOptions() {
  const { t } = useTranslation();
  return [
    { value: '', label: t('common.sources.allSources') },
    { value: 'manual', label: t('common.sources.manual') },
    { value: 'website', label: t('common.sources.website') },
    { value: 'referral', label: t('common.sources.referral') },
    { value: 'email', label: t('common.sources.email') },
    { value: 'linkedin', label: t('common.sources.linkedin') },
    { value: 'web_form', label: t('common.sources.form') },
    { value: 'scraping', label: t('common.sources.scraping') },
  ];
}

function useCapacityOptions() {
  const { t } = useTranslation();
  return [
    { value: '', label: t('leads.list.allCapacities') },
    { value: 'High', label: t('leads.list.capacityHigh') },
    { value: 'Medium', label: t('leads.list.capacityMedium') },
    { value: 'Low', label: t('leads.list.capacityLow') },
  ];
}

interface TextFilterRowProps {
  label: string;
  value: string;
  onChangeText: (value: string) => void;
  placeholder: string;
  testID: string;
}

function TextFilterRow({ label, value, onChangeText, placeholder, testID }: TextFilterRowProps) {
  const [focused, setFocused] = useState(false);

  return (
    <HStack spacing="sm" align="center">
      <Text variant="caption" color={colors.text.secondary} style={styles.filterLabel}>
        {label}
      </Text>
      <Box style={[styles.textFilterInput, focused && styles.textFilterInputFocused]}>
        <FormInput
          value={value}
          onChangeText={onChangeText}
          placeholder={placeholder}
          style={styles.filterTextInput}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          autoCapitalize="none"
          autoCorrect={false}
          testID={testID}
          accessibilityLabel={label}
        />
      </Box>
    </HStack>
  );
}

export function LeadFilters({
  sourceFilter,
  onSourceChange,
  stageFilter,
  onStageChange,
  stageOptions,
  sortBy,
  sortOrder,
  onSortChange,
  startDate,
  onStartDateChange,
  endDate,
  onEndDateChange,
  jobTitleFilter,
  onJobTitleChange,
  industryFilter,
  onIndustryChange,
  economicCapacityFilter,
  onEconomicCapacityChange,
  locationFilter,
  onLocationChange,
  tagFilter,
  onTagChange,
  tagOptions,
  onClearFilters,
  hasActiveFilters,
}: Readonly<LeadFiltersProps>) {
  const { t } = useTranslation();
  const sourceOptions = useSourceOptions();
  const capacityOptions = useCapacityOptions();

  const sortValue = `${sortBy}:${sortOrder}`;

  const sortOptions = SORT_OPTIONS.map((opt) => ({
    value: opt.value,
    label: t(opt.labelKey),
  }));

  const handleSortChange = useCallback(
    (value: string) => {
      const [field, direction] = value.split(':');
      onSortChange(field, direction as 'asc' | 'desc');
    },
    [onSortChange],
  );

  return (
    <VStack spacing="sm">
      <Select
        variant="chips"
        options={sourceOptions}
        value={sourceFilter}
        onChange={onSourceChange}
        testID="leads-source-filter"
      />

      {stageOptions.length > 1 && (
        <Select
          variant="chips"
          options={stageOptions}
          value={stageFilter}
          onChange={onStageChange}
          testID="leads-stage-filter"
        />
      )}

      <HStack spacing="sm" align="center">
        <Text variant="caption" color={colors.text.secondary} style={styles.filterLabel}>
          {t('leads.list.economicCapacity')}
        </Text>
        <Box style={styles.flexFill}>
          <Select
            variant="chips"
            options={capacityOptions}
            value={economicCapacityFilter}
            onChange={onEconomicCapacityChange}
            testID="leads-capacity-filter"
          />
        </Box>
      </HStack>

      <TextFilterRow
        label={t('leads.list.jobTitle')}
        value={jobTitleFilter}
        onChangeText={onJobTitleChange}
        placeholder={t('leads.list.jobTitle')}
        testID="leads-jobtitle-filter"
      />

      <TextFilterRow
        label={t('leads.list.industry')}
        value={industryFilter}
        onChangeText={onIndustryChange}
        placeholder={t('leads.list.industry')}
        testID="leads-industry-filter"
      />

      <TextFilterRow
        label={t('leads.list.location')}
        value={locationFilter}
        onChangeText={onLocationChange}
        placeholder={t('leads.list.location')}
        testID="leads-location-filter"
      />

      {tagOptions.length > 0 && (
        <HStack spacing="sm" align="center">
          <Text variant="caption" color={colors.text.secondary} style={styles.filterLabel}>
            {t('leads.list.tags')}
          </Text>
          <Box style={styles.flexFill}>
            <Select
              variant="chips"
              multiple
              options={tagOptions}
              value={tagFilter}
              onChange={onTagChange}
              testID="leads-tag-filter"
            />
          </Box>
        </HStack>
      )}

      <HStack spacing="sm" align="center">
        <Text variant="caption" color={colors.text.secondary} style={styles.filterLabel}>
          {t('leads.list.sortBy')}
        </Text>
        <Box style={styles.flexFill}>
          <Select
            variant="chips"
            options={sortOptions}
            value={sortValue}
            onChange={handleSortChange}
            testID="leads-sort"
          />
        </Box>
      </HStack>

      <HStack spacing="sm" align="center">
        <Text variant="caption" color={colors.text.secondary} style={styles.filterLabel}>
          {t('leads.list.dateRange')}
        </Text>
        <DatePickerInput
          value={startDate}
          onChange={onStartDateChange}
          placeholder={t('leads.list.startDate')}
          testID="leads-start-date"
        />
        <Text variant="caption" color={colors.text.tertiary}>
          —
        </Text>
        <DatePickerInput
          value={endDate}
          onChange={onEndDateChange}
          placeholder={t('leads.list.endDate')}
          testID="leads-end-date"
        />
      </HStack>

      {hasActiveFilters && (
        <Pressable onPress={onClearFilters} style={styles.clearButton}>
          <HStack spacing="xs" align="center">
            <X size={12} color={colors.text.secondary} strokeWidth={2} />
            <Text variant="caption" color={colors.text.secondary} style={styles.clearLabel}>
              {t('leads.list.clearFilters')}
            </Text>
          </HStack>
        </Pressable>
      )}
    </VStack>
  );
}

const styles = StyleSheet.create({
  filterLabel: {
    fontWeight: '500',
    minWidth: 60,
  },
  flexFill: {
    flex: 1,
  },
  textFilterInput: {
    borderRadius: tokens.radius.md,
    borderWidth: 1,
    borderColor: colors.border.input,
    backgroundColor: colors.background.input,
    paddingHorizontal: tokens.spacing.sm,
    minHeight: 36,
    justifyContent: 'center',
    flex: 1,
  },
  textFilterInputFocused: {
    borderColor: colors.primary.main,
  },
  filterTextInput: {
    color: colors.text.primary,
    fontSize: tokens.fontSize.sm,
    minHeight: 20,
    ...Platform.select({
      web: {
        outlineStyle: 'none',
      } as unknown as object,
    }),
  },
  clearButton: {
    alignSelf: 'flex-start',
    paddingVertical: tokens.spacing.xs,
    paddingHorizontal: tokens.spacing.sm,
  },
  clearLabel: {
    fontWeight: '500',
  },
});
